/**
 * Chat Service - Handles communication with n8n chatbot via backend proxy
 * Smart Doc Pro Chatbot Integration
 */

const CHAT_API_ENDPOINT = '/api/chat/openai.php';
const REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Get auth token from localStorage
 */
function getAuthToken() {
  try {
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      return parsed.state?.token || null;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return null;
}

/**
 * Get stable user ID for persistent chat sessions
 * @returns {string} - Stable user ID
 */
function getUserId() {
  try {
    // Try to get logged-in user ID from auth storage
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      const user = parsed.state?.user;
      if (user?.id) {
        return `user_${user.id}`;
      }
      if (user?.email) {
        return `user_${user.email.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
    }
  } catch (error) {
    console.error('Error getting user ID from auth:', error);
  }

  // Fallback: Generate and persist a stable browser-based ID
  let browserId = localStorage.getItem('chat-browser-id');
  if (!browserId) {
    // Use crypto.randomUUID() if available, otherwise fallback
    browserId = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID() 
      : `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('chat-browser-id', browserId);
    console.log('🔵 [CHATBOT DEBUG] Generated new browser ID:', browserId);
  }
  
  return `guest_${browserId}`;
}

/**
 * Send a message to the chatbot and get a response
 * @param {string} message - The user's message
 * @returns {Promise<{answer: string}>} - The chatbot's response
 * @throws {Error} - If the request fails
 */
export async function sendChatMessage(message) {
  if (!message || typeof message !== 'string' || message.trim().length === 0) {
    throw new Error('Message cannot be empty');
  }

  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }

  // 🔥 FIX: Get stable userId for persistent n8n memory sessions
  const userId = getUserId();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    console.log('🔵 [CHATBOT DEBUG] Sending message to backend:', {
      endpoint: CHAT_API_ENDPOINT,
      userId: userId,
      message: message.trim(),
      hasToken: !!token
    });

    const response = await fetch(CHAT_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        userId: userId,           // 🔥 FIX: Include stable userId for n8n memory
        message: message.trim()   // User's chat message
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    console.log('🔵 [CHATBOT DEBUG] Backend response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('🔴 [CHATBOT DEBUG] Error response:', errorData);
      throw new Error(errorData.error || `Chat API error: ${response.status}`);
    }

    // Get raw response text first
    const rawText = await response.text();
    console.log('🔵 [CHATBOT DEBUG] Raw response text:', rawText);

    // Try to parse JSON
    let data;
    try {
      data = JSON.parse(rawText);
      console.log('🔵 [CHATBOT DEBUG] Parsed JSON:', data);
    } catch (parseError) {
      console.error('🔴 [CHATBOT DEBUG] JSON parse error:', parseError);
      throw new Error('Failed to parse response as JSON');
    }

    if (!data || typeof data.answer !== 'string') {
      console.error('🔴 [CHATBOT DEBUG] Invalid response structure:', data);
      throw new Error('Invalid response format from chatbot');
    }

    console.log('✅ [CHATBOT DEBUG] Successfully received answer:', data.answer.substring(0, 100) + '...');
    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection.');
    }

    throw error;
  }
}

/**
 * Test the chatbot connection
 * @returns {Promise<boolean>} - True if connection is successful
 */
export async function testConnection() {
  try {
    await sendChatMessage('Hello');
    return true;
  } catch (error) {
    console.error('Chatbot connection test failed:', error);
    return false;
  }
}

export default {
  sendChatMessage,
  testConnection,
};
